/**
 * cdn-domain-purge-warm-tab.tsx — Purge & Warm tab for a CDN domain (act-as).
 *
 * Mirrors Partner's PurgeWarmTab.tsx:
 *   - Purge card: mode selector (url|prefix|suffix|tag|all), textarea/input,
 *     all-purge confirmation checkbox, propagation status badge (polling).
 *   - Warm card: URL textarea → POST /warm.
 *
 * Every submission goes through ActAsMutationConfirm (reason required).
 * Purge-all uses `destructive` mode.
 * After purge submit, polls GET /purge/:purgeId every 3 s until terminal.
 *
 * Endpoints:
 *   POST /api/v3/cdn/domains/:id/purge
 *   GET  /api/v3/cdn/domains/:id/purge/:purgeId   (status polling via hook)
 *   POST /api/v3/cdn/domains/:id/warm
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flame, Wind, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Section, SettingRow } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  Skeleton,
  Badge,
  Switch,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useActAsCDNPurgeStatus,
  actAsCDNRulesKeys,
  type PurgeMode,
  type PurgeResponse,
} from '@/api/act-as-cdn-rules';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';

/* ── Purge mode options ──────────────────────────────────────────────────── */

const PURGE_MODES: { value: PurgeMode; label: string; placeholder: string; multi: boolean }[] = [
  {
    value: 'url',
    label: 'By URL',
    placeholder: 'https://example.com/path/to/file.jpg\nhttps://example.com/other.css',
    multi: true,
  },
  {
    value: 'prefix',
    label: 'By prefix',
    placeholder: '/assets/\n/images/',
    multi: true,
  },
  {
    value: 'suffix',
    label: 'By suffix',
    placeholder: '.css\n.js',
    multi: true,
  },
  {
    value: 'tag',
    label: 'By cache tag',
    placeholder: 'my-tag\nproduct-123',
    multi: true,
  },
  {
    value: 'all',
    label: 'Purge all',
    placeholder: '',
    multi: false,
  },
];

/* ── Purge status pill ───────────────────────────────────────────────────── */

function PurgeStatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  if (status === 'completed') {
    return (
      <Badge tone="success" appearance="subtle">
        <CheckCircle2 size={12} style={{ marginRight: 4 }} />
        {t('cdn_domain.purge.status_completed', 'Completed')}
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge tone="danger" appearance="subtle">
        <XCircle size={12} style={{ marginRight: 4 }} />
        {t('cdn_domain.purge.status_failed', 'Failed')}
      </Badge>
    );
  }
  return (
    <Badge tone="warning" appearance="subtle">
      <Loader2 size={12} style={{ marginRight: 4, animation: 'spin 1s linear infinite' }} />
      {t('cdn_domain.purge.status_propagating', 'Propagating…')}
    </Badge>
  );
}

/* ── Purge card ──────────────────────────────────────────────────────────── */

function PurgeCard({ domainId }: { domainId: string }) {
  const { t } = useTranslation();

  const [mode, setMode] = useState<PurgeMode>('url');
  const [valuesText, setValuesText] = useState('');
  const [allConfirmed, setAllConfirmed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activePurgeId, setActivePurgeId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);

  const modeOpt = PURGE_MODES.find((m) => m.value === mode)!;
  const isAll = mode === 'all';
  const canSubmit = isAll ? allConfirmed : valuesText.trim().length > 0;

  const purgeStatusQ = useActAsCDNPurgeStatus(domainId, activePurgeId, polling);

  const buildPayload = () => {
    if (isAll) return { mode: 'all' as const };
    const values = valuesText
      .split('\n')
      .map((v) => v.trim())
      .filter(Boolean);
    return { mode, values };
  };

  const handleSuccess = () => {
    // Reset form after mutationFn resolves; purge ID comes back in mutationFn response.
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <Flame size={16} style={{ color: 'var(--vn-fg-muted)' }} />
          {t('cdn_domain.purge.card_title', 'Purge cache')}
        </CardTitle>
        <CardDescription>
          {t(
            'cdn_domain.purge.card_desc',
            'Invalidate cached objects from all edge nodes. Propagation takes 10–30 seconds.',
          )}
        </CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Field label={t('cdn_domain.purge.field_mode', 'Purge mode')}>
          {(props) => (
            <Select
              value={mode}
              onValueChange={(v) => {
                setMode(v as PurgeMode);
                setValuesText('');
                setAllConfirmed(false);
              }}
            >
              <SelectTrigger {...props}><SelectValue /></SelectTrigger>
              <SelectContent>
                {PURGE_MODES.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        {!isAll && (
          <Field
            label={t('cdn_domain.purge.field_values', 'Values')}
            description={t('cdn_domain.purge.field_values_desc', 'One entry per line.')}
          >
            {(props) => (
              <Textarea
                {...props}
                value={valuesText}
                placeholder={modeOpt.placeholder}
                rows={5}
                onChange={(e) => setValuesText(e.target.value)}
              />
            )}
          </Field>
        )}

        {isAll && (
          <Alert tone="warning">
            {t(
              'cdn_domain.purge.all_warning',
              'Purge all will invalidate every cached object for this domain across all edge nodes. This may cause a temporary increase in origin traffic.',
            )}
          </Alert>
        )}

        {isAll && (
          <SettingRow
            title={t('cdn_domain.purge.all_confirm_label', 'I understand the impact of purging all cached objects')}
            control={
              <Switch checked={allConfirmed} onCheckedChange={setAllConfirmed} />
            }
          />
        )}

        {/* Propagation status */}
        {activePurgeId && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              {t('cdn_domain.purge.last_job', 'Last purge job:')}
            </span>
            {purgeStatusQ.isLoading ? (
              <Skeleton style={{ width: 80, height: 20 }} />
            ) : (
              <PurgeStatusPill status={purgeStatusQ.data?.status ?? 'pending'} />
            )}
          </div>
        )}

        <div>
          <Button
            disabled={!canSubmit}
            variant={isAll ? 'danger' : 'primary'}
            onClick={() => setConfirmOpen(true)}
          >
            {t('cdn_domain.purge.action_purge', 'Purge')}
          </Button>
        </div>
      </CardBody>

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={
          isAll
            ? t('cdn_domain.purge.confirm_all_title', 'Purge all cached objects')
            : t('cdn_domain.purge.confirm_title', 'Purge cache')
        }
        summary={
          isAll
            ? t('cdn_domain.purge.confirm_all_summary', 'Every cached object for this domain will be invalidated.')
            : t('cdn_domain.purge.confirm_summary', 'Purge mode: {{mode}}. {{n}} entr(ies) will be invalidated.', {
                mode,
                n: valuesText.split('\n').filter((l) => l.trim()).length,
              })
        }
        queryKeysToInvalidate={
          activePurgeId ? [actAsCDNRulesKeys.purgeStatus(domainId, activePurgeId)] : []
        }
        mutationFn={async () => {
          const payload = buildPayload();
          const res = await actAsClient.post<{ data?: PurgeResponse } | PurgeResponse>(
            `/cdn/domains/${domainId}/purge`,
            payload,
          );
          const body = res.data as Record<string, unknown>;
          const inner = (body.data ?? body) as PurgeResponse;
          if (inner.purge_id) {
            setActivePurgeId(inner.purge_id);
            setPolling(true);
          }
        }}
        onSuccess={() => {
          setValuesText('');
          setAllConfirmed(false);
          handleSuccess();
        }}
        destructive={isAll}
        confirmLabel={t('cdn_domain.purge.action_purge', 'Purge')}
      />
    </Card>
  );
}

/* ── Warm card ───────────────────────────────────────────────────────────── */

function WarmCard({ domainId }: { domainId: string }) {
  const { t } = useTranslation();
  const [urlsText, setUrlsText] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const urls = urlsText
    .split('\n')
    .map((u) => u.trim())
    .filter(Boolean);
  const canSubmit = urls.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <Wind size={16} style={{ color: 'var(--vn-fg-muted)' }} />
          {t('cdn_domain.purge.warm_card_title', 'Warm cache')}
        </CardTitle>
        <CardDescription>
          {t(
            'cdn_domain.purge.warm_card_desc',
            'Pre-fetch URLs into the edge cache so they are ready for the first visitor.',
          )}
        </CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Field
          label={t('cdn_domain.purge.warm_field_urls', 'URLs to warm')}
          description={t('cdn_domain.purge.warm_field_urls_desc', 'One URL per line.')}
        >
          {(props) => (
            <Textarea
              {...props}
              value={urlsText}
              placeholder={'https://example.com/hero-image.jpg\nhttps://example.com/styles.css'}
              rows={5}
              onChange={(e) => setUrlsText(e.target.value)}
            />
          )}
        </Field>
        <div>
          <Button disabled={!canSubmit} onClick={() => setConfirmOpen(true)}>
            {t('cdn_domain.purge.action_warm', 'Warm')}
          </Button>
        </div>
      </CardBody>

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.purge.confirm_warm_title', 'Warm cache')}
        summary={t(
          'cdn_domain.purge.confirm_warm_summary',
          '{{n}} URL(s) will be pre-fetched into the edge cache.',
          { n: urls.length },
        )}
        queryKeysToInvalidate={[]}
        mutationFn={() =>
          actAsClient.post(`/cdn/domains/${domainId}/warm`, { urls })
        }
        onSuccess={() => setUrlsText('')}
        confirmLabel={t('cdn_domain.purge.action_warm', 'Warm')}
      />
    </Card>
  );
}

/* ── Tab component ───────────────────────────────────────────────────────── */

interface CdnDomainPurgeWarmTabProps {
  domainId: string;
}

export function CdnDomainPurgeWarmTab({ domainId }: CdnDomainPurgeWarmTabProps) {
  const { t } = useTranslation();

  return (
    <Section
      title={t('cdn_domain.purge.title', 'Purge & Warm')}
      description={t(
        'cdn_domain.purge.description',
        'Invalidate stale cached content and pre-warm the edge cache for upcoming traffic.',
      )}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <PurgeCard domainId={domainId} />
        <WarmCard domainId={domainId} />
      </div>
    </Section>
  );
}
